/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { debugPixel, encodeJsonBase64 } from '../util';

import type { FailedCondition, Facts } from './index';

/** The rule scope a reported failure was evaluated at. */
export type RuleFailureLevel = 'p' | 'c' | 'a';

// Payload caps for the rdbg debug pixel (bounded GET URL)
const DEBUG_MAX_ARRAY_ENTRIES = 10;
const DEBUG_MAX_STRING_LENGTH = 100;
const DEBUG_MAX_BLOB_LENGTH = 1200;
// Safety valve: bounds dedup memory and pixel volume under pathological rules
const DEBUG_MAX_SIGNATURES = 50;

const reportedSignaturesByDid = new Map<string, Set<string>>();

/**
 * Sends an `rdbg` debug pixel describing a failing rule evaluation for a
 * dbg-flagged ad. Deduped per distinct (placement, level, failure signature)
 * per delivery id for the page lifetime: the signature covers
 * condition/state/fact/op but not actual values, so a volatile fact value
 * never re-fires — only a change in WHICH conditions fail, or the same
 * failure on another placement, re-reports. Property- and
 * campaign-level gate failures report once per gate, attributed to a
 * dbg-flagged ad beneath it. Fires in production builds (forceLog) since dbg
 * is server-set.
 *
 * `facts` is the exact fact layer the rules ran against, so the reported ad
 * unit identifies the placement whose evaluation failed.
 */
export const reportRuleFailure = (
  did: string,
  facts: Facts,
  failures: FailedCondition[],
  level: RuleFailureLevel,
): void => {
  const adUnitCode = String(facts['placement.code'] ?? '');
  const signature =
    `${adUnitCode}|${level}|` +
    failures
      .map((failure) => `${failure.cond}:${failure.st}:${failure.fact}:${failure.op}`)
      .sort()
      .join('|');
  let signatures = reportedSignaturesByDid.get(did);
  if (!signatures) {
    signatures = new Set<string>();
    reportedSignaturesByDid.set(did, signatures);
  }
  if (signatures.has(signature) || signatures.size >= DEBUG_MAX_SIGNATURES) {
    return;
  }
  signatures.add(signature);

  let encoded = encodeJsonBase64(failures.map((failure) => toDebugEntry(failure, level, true)));
  if (encoded.length > DEBUG_MAX_BLOB_LENGTH) {
    // fact/op/state alone always fits and is the diagnostically essential part
    encoded = encodeJsonBase64(failures.map((failure) => toDebugEntry(failure, level, false)));
  }
  // gad is the Prebid ad unit code everywhere else it is reported
  const params: Record<string, string> = {
    en: 'rdbg',
    did,
    gad: adUnitCode,
    d: encoded,
  };
  // Absent until the ad server slot is found, and the fact rules target by path
  const adUnitPath = facts['placement.adunit'];
  if (adUnitPath) {
    params.aup = String(adUnitPath);
  }
  debugPixel(params, true);
};

export const _resetRuleFailureReportsForTests = (): void => {
  reportedSignaturesByDid.clear();
};

interface DebugEntry {
  cond: string;
  fact: string;
  op: string;
  st: string;
  lvl: RuleFailureLevel;
  a?: unknown;
}

const toDebugEntry = (
  failure: FailedCondition,
  level: RuleFailureLevel,
  includeActual: boolean,
): DebugEntry => {
  const entry: DebugEntry = {
    cond: failure.cond,
    fact: failure.fact,
    op: failure.op,
    st: failure.st,
    lvl: level,
  };
  if (includeActual && failure.st === 'F' && failure.actual !== undefined) {
    entry.a = capActualValue(failure.actual);
  }
  return entry;
};

const capActualValue = (actual: unknown): unknown => {
  if (Array.isArray(actual)) {
    if (actual.length <= DEBUG_MAX_ARRAY_ENTRIES) {
      return actual;
    }
    const capped: unknown[] = actual.slice(0, DEBUG_MAX_ARRAY_ENTRIES);
    capped.push(`+${actual.length - DEBUG_MAX_ARRAY_ENTRIES}`);
    return capped;
  }
  if (typeof actual === 'string') {
    return actual.length > DEBUG_MAX_STRING_LENGTH
      ? actual.slice(0, DEBUG_MAX_STRING_LENGTH)
      : actual;
  }
  if (typeof actual === 'object' && actual !== null) {
    return JSON.stringify(actual).slice(0, DEBUG_MAX_STRING_LENGTH);
  }
  return actual;
};
